import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { Loader2, ChevronDown, Search } from "lucide-react";

/**
 * Champ de recherche Bedee avec dropdown cliquable.
 * type = "entreprise" | "contact"
 * - Dropdown rendu via Portal (hors de tout overflow:hidden)
 * - Affiche les premiers résultats dès le clic
 * - Filtre en temps réel (debounce 300ms)
 */
export default function BedeeSearchField({
  type,
  value,
  onChange,
  onSelect,       // callback(item) avec l'objet complet
  entrepriseId,   // filtre contact par entreprise
  placeholder,
  className = "",
  "data-testid": testId,
}) {
  const [query, setQuery] = useState(value || "");
  const [committed, setCommitted] = useState(value || ""); // valeur "validée"
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const timer = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Recalcule la position du dropdown (fixed, hors overflow:hidden)
  const updateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 2,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  }, []);

  // Fermer au clic extérieur
  useEffect(() => {
    const handler = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        !e.target.closest("[data-bedee-dropdown]")
      ) {
        // Annuler la frappe partielle : revenir à la valeur validée
        setQuery(committed);
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [committed]);

  // Sync valeur externe (OCR / pré-remplissage)
  useEffect(() => {
    setQuery(value || "");
    setCommitted(value || "");
  }, [value]);

  // Quand le filtre entreprise change, vider les suggestions du contact
  useEffect(() => {
    setSuggestions([]);
    setOpen(false);
  }, [entrepriseId]);

  const getEndpoint = (q) => {
    if (type === "entreprise") {
      return `/contacts/bedee-entreprises?q=${encodeURIComponent(q || "")}`;
    }
    // contact : filtrer par entreprise si disponible
    const base = `/contacts/bedee-contacts?q=${encodeURIComponent(q || "")}`;
    return entrepriseId ? `${base}&entreprise_id=${encodeURIComponent(entrepriseId)}` : base;
  };

  // Charger au focus (premiers résultats immédiats)
  const handleFocus = () => {
    updateDropdownPosition();
    // Si la valeur actuelle est longue, chercher avec, sinon charger les premiers
    const searchQ = query && query.length >= 2 ? query : "";
    setLoading(true);
    api.get(getEndpoint(searchQ))
      .then((r) => {
        setSuggestions(r.data.slice(0, 15));
        setOpen(true);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Recherche à la saisie — met à jour query pour l'affichage mais PAS onChange
  // (onChange n'est appelé que sur sélection ou clear explicite)
  const search = (q) => {
    setQuery(q);
    clearTimeout(timer.current);
    updateDropdownPosition();
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get(getEndpoint(q))
        .then((r) => {
          setSuggestions(r.data.slice(0, 15));
          setOpen(true);
        })
        .catch(() => setSuggestions([]))
        .finally(() => setLoading(false));
    }, 300);
  };

  const select = (item) => {
    const label =
      type === "entreprise"
        ? item.nom
        : `${item.prenom ? item.prenom + " " : ""}${item.nom || ""}`.trim();
    setQuery(label);
    setCommitted(label);
    onChange(label);
    onSelect?.(item);   // transmet l'objet complet au parent (id, nom, …)
    setOpen(false);
  };

  const dropdown = open && (
    <ul
      data-bedee-dropdown="true"
      style={dropdownStyle}
      className="bg-white border border-zinc-200 shadow-xl max-h-56 overflow-y-auto"
    >
      {loading && suggestions.length === 0 && (
        <li className="px-3 py-2 text-xs text-zinc-400 flex items-center gap-2">
          <Loader2 size={11} className="animate-spin" /> Chargement…
        </li>
      )}
      {!loading && suggestions.length === 0 && (
        <li className="px-3 py-2 text-xs text-zinc-400 italic">Aucun résultat</li>
      )}
      {suggestions.map((item) => {
        const label =
          type === "entreprise"
            ? item.nom
            : `${item.prenom ? item.prenom + " " : ""}${item.nom || ""}`.trim();
        const sub =
          type === "entreprise"
            ? null
            : item.entreprise_nom || item.email || null;
        return (
          <li
            key={item.id}
            onMouseDown={(e) => { e.preventDefault(); select(item); }}
            className="px-3 py-2 text-sm cursor-pointer hover:bg-[#002FA7]/5 flex items-center justify-between gap-2 border-b border-zinc-50 last:border-0"
          >
            <span className="font-medium truncate">{label}</span>
            {sub && (
              <span className="text-xs text-zinc-400 shrink-0 truncate max-w-[45%]">{sub}</span>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative flex items-center">
        <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid={testId}
          type="text"
          value={query}
          onChange={(e) => search(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (open && suggestions.length > 0) {
                select(suggestions[0]);        // Sélectionner le premier résultat avec Entrée
              } else if (!open && query === "") {
                setCommitted("");
                onChange("");                   // Vider explicitement avec Entrée sur champ vide
              }
            } else if (e.key === "Escape") {
              setQuery(committed);              // Annuler la frappe
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          className={`${className} pl-7 pr-8`}
          autoComplete="off"
          maxLength={200}
        />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            if (open) {
              setOpen(false);
            } else {
              inputRef.current?.focus();
            }
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
          tabIndex={-1}
        >
          {loading
            ? <Loader2 size={13} className="animate-spin" />
            : <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          }
        </button>
      </div>

      {typeof document !== "undefined" && createPortal(dropdown, document.body)}
    </div>
  );
}
